import {
  type ComponentPropsWithoutRef,
  cloneElement,
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  useId,
} from 'react';
import {
  FieldCaption,
  FieldDescription,
  FieldError,
  FieldInput,
  FieldLabel,
  FieldMessages,
  FieldRequired,
  FieldRoot,
} from '../../components/Field';

export type FieldßProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * The label for the input field.
   * @example 'Email'
   */
  label?: ReactNode;
  /**
   * The type of input component to render.
   * @example 'Inputß'
   */
  as?: 'Inputß' | 'text' | 'number' | 'date' | 'multiline';
  /**
   * Additional messages or descriptions for the field.
   * Useful for hints or guidance text.
   */
  description?: ReactNode;
  /**
   * Error message to display when validation fails.
   */
  error?: ReactNode;
  /**
   * Indicates if the field is required.
   */
  required?: boolean;
  /**
   * Additional caption text for the field.
   */
  caption?: ReactNode;
  /**
   * Custom className for styling.
   */
  className?: string;
  /**
   * The children for the field, typically an input or custom input component.
   */
  children: ReactNode;

  /**
   * Test identifier for testing purposes.
   * @example 'my-field'
   */
  'data-testid'?: string;
};

/**
 * A `Fieldß` component that wraps input components with a label, description, error, and caption.
 * It reuses existing `Field` components to maintain consistency.
 * @beta
 * @example
 * <Fieldß label="Name" as="Inputß" required>
 *   <Inputß as="text" />
 * </Fieldß>
 */
export const Fieldß = forwardRef<HTMLDivElement, FieldßProps>(
  (
    {
      label,
      as = 'Inputß',
      description,
      error,
      required,
      caption,
      className,
      children,
      'data-testid': testId = 'field',
      ...rest
    },
    ref,
  ) => {
    const inputId = useId();
    const descriptionId = useId();
    const errorId = useId();
    const captionId = useId();

    const inputElement = children
      ? cloneElement(
          children as React.ReactElement<
            HTMLAttributes<unknown> & {
              required: boolean;
              'data-testid'?: string;
            }
          >,
          {
            id: inputId,
            'aria-describedby': description ? descriptionId : undefined,
            'aria-errormessage': error ? errorId : undefined,
            'aria-invalid': !!error,
            required,
            'data-testid': `${testId}-input`,
          },
        )
      : null;

    const renderInput = () => {
      // If `as` is `Inputß`, wrap the child with `FieldInput`.
      if (as === 'Inputß') {
        return (
          <FieldInput
            asChild
            id={inputId}
            required={required}
            data-testid={`${testId}-input-wrapper`}
          >
            {inputElement}
          </FieldInput>
        );
      }
      // For other input types, render directly.
      return inputElement;
    };

    return (
      <FieldRoot ref={ref} className={className} data-testid={testId} {...rest}>
        {label && (
          <FieldLabel htmlFor={inputId} data-testid={`${testId}-label`}>
            {label}
            {required && (
              <FieldRequired data-testid={`${testId}-required`}>
                *
              </FieldRequired>
            )}
          </FieldLabel>
        )}
        {caption && (
          <FieldCaption id={captionId} data-testid={`${testId}-caption`}>
            {caption}
          </FieldCaption>
        )}
        {renderInput()}
        <FieldMessages data-testid={`${testId}-messages`}>
          {description && (
            <FieldDescription
              id={descriptionId}
              data-testid={`${testId}-description`}
            >
              {description}
            </FieldDescription>
          )}
          {error && (
            <FieldError id={errorId} data-testid={`${testId}-error`}>
              {error}
            </FieldError>
          )}
        </FieldMessages>
      </FieldRoot>
    );
  },
);
